<!--
@component
Matches

See markup below.
-->

<script lang="ts">
  import type { Match, MatchesData } from '$lib/@types';

  import { Block } from '@reuters-graphics/graphics-components';
  import MatchCard from '$lib/components/common/MatchCard/index.svelte';
  import { appState } from '$lib/stores/index.svelte';
  import { isPlaying, isUpcoming } from '$utils/getMatchStats';

  type Team = { id: string; code: string; [key: string]: unknown };

  let { data, team }: { data: MatchesData; team: Team } = $props();

  // `data` is accepted for prop compatibility; matches come from the store.
  void data;

  let allMatches: Match[] = $derived(appState.matches as Match[]);

  /**
   * Unique stage names in feed order — e.g. `'Group Stage'`, `'Last 16'`, …
   * Used to group each section below in the same order Opta delivers stages.
   */
  let matchTypes: string[] = $derived(
    Array.from(
      new Set(
        allMatches
          .map((m) => m.matchInfo?.stage?.name)
          .filter((name): name is string => typeof name === 'string')
      )
    )
  );

  /** Does this match include the current team as one of its contestants? */
  const involvesTeam = (match: Match): boolean => {
    const contestants = match.matchInfo?.contestant;
    if (!Array.isArray(contestants)) return false;
    return contestants.some((c) => c?.code === team?.code);
  };

  /**
   * Single-pass bucketing (issue #30).
   *
   * Previously we ran three separate `.filter()` passes (one each for
   * live / results / fixtures) and then `groupByStage` called another
   * `.filter()` per stage — O(types × matches) per bucket. We now walk
   * `allMatches` once, route each match into the right bucket, and
   * collect into a `Map<stage, Match[]>` keyed by stage name so the
   * stage-grouping step is also a single pass.
   *
   * Fixtures still need to be reversed (nearest-first) and filtered to
   * fully-contested matches, both of which preserve the previous
   * component's visible output.
   */
  const groupByStageMap = (matches: Match[]) => {
    const byStage = new Map<string, Match[]>();
    for (const m of matches) {
      const stage = m.matchInfo?.stage?.name;
      if (typeof stage !== 'string') continue;
      const bucket = byStage.get(stage);
      if (bucket) bucket.push(m);
      else byStage.set(stage, [m]);
    }
    // Preserve feed order of stages: walk `matchTypes` (already built
    // from a feed-order Set) and emit only stages that have matches.
    return matchTypes
      .map((type) => ({ type, matches: byStage.get(type) ?? [] }))
      .filter((g) => g.matches.length > 0);
  };

  const buckets = $derived.by(() => {
    const live: Match[] = [];
    const results: Match[] = [];
    const fixtures: Match[] = [];
    for (const m of allMatches) {
      if (!involvesTeam(m)) continue;
      if (isPlaying(m)) {
        // Live: not upcoming AND is currently playing.
        if (!isUpcoming(m)) live.push(m);
      } else if (isUpcoming(m)) {
        // Fixtures: upcoming AND fully-contested (two contestants).
        const contestants = m.matchInfo?.contestant;
        if (Array.isArray(contestants) && contestants.length === 2) {
          fixtures.push(m);
        }
      } else {
        // Results: everything else (already played).
        results.push(m);
      }
    }
    // Match the previous "earliest-first feed reversed" semantics so the
    // nearest fixture appears first.
    fixtures.reverse();
    return {
      liveByStage: groupByStageMap(live),
      fixturesByStage: groupByStageMap(fixtures),
      resultsByStage: groupByStageMap(results),
    };
  });

  let liveByStage = $derived(buckets.liveByStage);
  let fixturesByStage = $derived(buckets.fixturesByStage);
  let resultsByStage = $derived(buckets.resultsByStage);
</script>

<!--
  `stageSection` renders one stage (e.g. "Group Stage" / "Last 16") of
  the team's match cards: stage title + divider, then a `MatchCard`
  per match. The live card has its own loading shimmer via
  `class:loading={appState.isLoading}`, so we don't need a placeholder
  layer here — the card handles its own loading visuals once it mounts.
-->
{#snippet stageSection(group: { type: string; matches: Match[] })}
  <section id={group.type}>
    <div class="match-stage fmb-3">
      <div class="hr"></div>
      <h4 class="name">{group.type}</h4>
      <div class="hr"></div>
    </div>

    <Block>
      {#each group.matches as match, i (match.matchInfo?.id ?? i)}
        <MatchCard linked={true} {match} {i} />
      {/each}
    </Block>
  </section>
{/snippet}

<!--
  Render the team's real sections + stages + `MatchCard`s. Each live
  card carries its own loading shimmer (`class:loading={appState.isLoading}`)
  so the loading state is handled per-card without a separate
  placeholder layer.

  `appState.matches` is populated by the route loader before this
  component mounts, so the buckets are reliable on first paint. If a
  bucket is empty its section simply isn't rendered — the correct
  empty state. See #23.
-->
<div class="matches">
  <!-- Live matches -->
  {#if liveByStage.length > 0}
    <section class="" id="live" aria-label="Live matches">
      {#each liveByStage as group (group.type)}
        {@render stageSection(group)}
      {/each}
    </section>
  {/if}

  <!-- Upcoming match fixtures -->
  {#if fixturesByStage.length > 0}
    <section class="" id="schedule" aria-label="Upcoming matches">
      <h2 class="text-center">Upcoming matches</h2>
      {#each fixturesByStage as group (group.type)}
        {@render stageSection(group)}
      {/each}
    </section>
  {/if}

  <!-- Played match results -->
  {#if resultsByStage.length > 0}
    <section class="" id="results" aria-label="Results">
      <h2 class="text-center">Results</h2>
      {#each resultsByStage as group (group.type)}
        {@render stageSection(group)}
      {/each}
    </section>
  {/if}
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  .matches {
    :global(.match) {
      @include mixins.fmy-7;
    }
  }

  .match-stage {
    @include mixins.match-stage;
  }
</style>
